import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Send, Sparkles, Upload } from 'lucide-react'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Markdown } from '~/components/ui/markdown'
import { ChatThinking } from '~/components/chat/chat-thinking'
import { shortId } from '~/lib/utils'
import { assistStream, getDashboard } from '~/api/monitoring'
import type { AssistCitation } from '~/api/types'

type Msg = {
    role: 'user' | 'assistant'
    content: string
    choices?: string[]
    citations?: AssistCitation[]
}

/** AI 어시스턴트 — 이탈 현황·HACCP 근거 기반 대화(SSE 실시간 스트리밍) + 입력폼 위 추천 선택지. */
export function AssistantSection({ dashboardId }: { dashboardId: string }) {
    const navigate = useNavigate()
    const [messages, setMessages] = useState<Msg[]>([])
    const [input, setInput] = useState('')
    const [busy, setBusy] = useState(false)
    // 적재된 측정값 개수: null=확인 중, 0=없음(빈 상태 안내), >0=대화 진행
    const [readingCount, setReadingCount] = useState<number | null>(null)
    const started = useRef(false)
    const endRef = useRef<HTMLDivElement>(null)

    const send = async (question: string, initial = false) => {
        if (busy) return
        if (!initial && !question.trim()) return
        const history = messages.map((m) => ({
            role: m.role,
            content: m.content,
        }))
        setMessages((prev) =>
            initial
                ? [...prev, { role: 'assistant', content: '' }]
                : [
                      ...prev,
                      { role: 'user', content: question },
                      { role: 'assistant', content: '' },
                  ]
        )
        setInput('')
        setBusy(true)

        const appendToken = (t: string) =>
            setMessages((prev) => {
                const next = [...prev]
                const last = next[next.length - 1]
                if (last && last.role === 'assistant') {
                    next[next.length - 1] = {
                        ...last,
                        content: last.content + t,
                    }
                }
                return next
            })
        const finish = (choices: string[], citations: AssistCitation[]) =>
            setMessages((prev) => {
                const next = [...prev]
                const last = next[next.length - 1]
                if (last && last.role === 'assistant') {
                    next[next.length - 1] = { ...last, choices, citations }
                }
                return next
            })

        try {
            await assistStream(
                dashboardId,
                { question, history },
                {
                    token: appendToken,
                    done: ({ choices, citations }) =>
                        finish(choices, citations),
                }
            )
        } catch {
            setMessages((prev) => {
                const next = [...prev]
                const last = next[next.length - 1]
                if (last && last.role === 'assistant' && !last.content) {
                    next[next.length - 1] = {
                        ...last,
                        content: '답변을 가져오지 못했어요.',
                    }
                }
                return next
            })
        } finally {
            setBusy(false)
        }
    }

    // 첫 진입: 적재 측정값 개수 확인 → 0건이면 잘못된 요약을 시작하지 않고 빈 상태 안내.
    // (StrictMode 이중 실행 가드는 started ref만 사용 — alive 플래그로 결과를 버리면
    //  첫 실행 결과가 폐기되고 둘째 실행은 가드에 막혀 영원히 로딩되는 문제가 있었다.)
    useEffect(() => {
        if (started.current) return
        started.current = true
        getDashboard(dashboardId)
            .then((d) => {
                const count = d.readingCount ?? 0
                setReadingCount(count)
                if (count > 0) void send('', true)
            })
            .catch(() => {
                // 개수 확인 실패 시엔 기존 흐름(요약 시작)을 유지한다.
                setReadingCount(1)
                void send('', true)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardId])

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages, busy])

    // 입력폼 위에 노출할 추천 선택지 = 마지막 어시스턴트 메시지의 choices.
    const lastAssistant = [...messages]
        .reverse()
        .find((m) => m.role === 'assistant')
    const choices = !busy ? (lastAssistant?.choices ?? []) : []

    const panelCitations = lastAssistant?.citations ?? []

    // 측정값 개수 확인 중: race 방지를 위해 로딩만 표시(요약 시작 안 함).
    if (readingCount === null) {
        return (
            <div className='text-muted-foreground flex h-full min-h-[420px] items-center justify-center text-sm'>
                <Loader2 className='mr-2 size-4 animate-spin' />
                불러오는 중…
            </div>
        )
    }

    // 적재 데이터가 0건: 잘못된 요약 대신 데이터 적재 CTA를 보여준다.
    if (readingCount === 0) {
        return (
            <div className='flex h-full min-h-[420px] items-center justify-center'>
                <div className='border-border bg-card flex max-w-md flex-col items-center gap-3 rounded-md border border-dashed p-8 text-center'>
                    <Sparkles className='text-primary size-7' />
                    <h2 className='text-foreground text-base font-semibold'>
                        아직 적재된 데이터가 없어요
                    </h2>
                    <p className='text-muted-foreground text-sm'>
                        측정값을 올리면 기준 이탈을 자동으로 점검하고 답해
                        드려요.
                    </p>
                    <Button
                        className='mt-1'
                        onClick={() =>
                            navigate(`/monitoring/${dashboardId}/settings`)
                        }>
                        <Upload className='size-4' />
                        데이터 적재
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className='flex h-full min-h-0 gap-3'>
            <section className='border-border bg-card flex h-full min-h-[420px] min-w-0 flex-1 flex-col rounded-md border'>
                <div className='border-border flex items-center gap-2 border-b px-5 py-3'>
                    <Sparkles className='text-primary size-4' />
                    <h2 className='text-foreground text-sm font-semibold'>
                        AI 어시스턴트
                    </h2>
                    <span className='text-muted-foreground text-xs'>
                        이탈 현황과 HACCP 근거로 답해 드려요
                    </span>
                </div>

                <div className='flex-1 space-y-3 overflow-y-auto p-5'>
                    {messages.map((m, i) => (
                        <div
                            key={i}
                            className={`flex w-full ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div
                                className={`max-w-[82%] min-w-0 rounded-md px-3.5 py-2.5 text-sm leading-relaxed ${
                                    m.role === 'user'
                                        ? 'bg-primary text-primary-foreground rounded-br-sm whitespace-pre-wrap'
                                        : 'border-border bg-background text-foreground rounded-bl-sm border'
                                }`}>
                                {m.content ? (
                                    m.role === 'user' ? (
                                        m.content
                                    ) : (
                                        <Markdown>{m.content}</Markdown>
                                    )
                                ) : m.role === 'assistant' && busy ? (
                                    <ChatThinking
                                        phrases={[
                                            '이탈 현황을 살펴보는 중',
                                            'HACCP 근거를 찾는 중',
                                            '답변을 정리하는 중',
                                        ]}
                                    />
                                ) : null}
                            </div>
                        </div>
                    ))}
                    <div ref={endRef} />
                </div>

                {/* 추천 선택지 — 입력폼 바로 위, 가볍게(칩) */}
                {choices.length > 0 ? (
                    <div className='border-border flex flex-wrap gap-1.5 border-t px-3 pt-2.5'>
                        {choices.map((c) => (
                            <button
                                key={c}
                                type='button'
                                onClick={() => void send(c)}
                                className='border-border text-muted-foreground hover:border-primary/40 hover:text-foreground rounded-full border bg-transparent px-3 py-1 text-xs transition-colors'>
                                {c}
                            </button>
                        ))}
                    </div>
                ) : null}

                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        void send(input)
                    }}
                    className='border-border flex items-center gap-2 border-t p-3'>
                    <input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder='직접 질문하거나 위 선택지를 눌러 보세요'
                        className='border-border bg-card text-foreground focus-visible:ring-ring flex-1 rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none'
                    />
                    <Button type='submit' disabled={busy || !input.trim()}>
                        {busy ? <Loader2 className='animate-spin' /> : <Send />}
                    </Button>
                </form>
            </section>

            {/* 근거 자료 — 항상 우측 별도 패널(넓은 화면) */}
            <aside className='border-border bg-card hidden h-full w-72 shrink-0 flex-col rounded-md border lg:flex'>
                <div className='border-border text-foreground border-b px-4 py-3 text-sm font-semibold'>
                    근거 자료
                </div>
                <div className='min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3'>
                    {panelCitations.length === 0 ? (
                        <p className='text-muted-foreground text-xs'>
                            답변의 근거(HACCP 기준·매뉴얼)가 여기에 표시됩니다.
                        </p>
                    ) : (
                        panelCitations.map((c, i) => (
                            <div
                                key={i}
                                className='border-border rounded-md border p-2.5'>
                                <div className='flex items-center gap-1.5'>
                                    <Badge tone='info'>근거 {i + 1}</Badge>
                                    <span className='text-foreground min-w-0 flex-1 truncate text-xs font-medium'>
                                        {c.title ||
                                            (c.documentId
                                                ? `문서 ${shortId(c.documentId)}`
                                                : '문서')}
                                    </span>
                                </div>
                                <div className='text-muted-foreground mt-1 flex flex-wrap gap-2 text-[11px]'>
                                    {c.clause ? (
                                        <span>조항 {c.clause}</span>
                                    ) : null}
                                    {c.page != null ? (
                                        <span>p.{c.page}</span>
                                    ) : null}
                                </div>
                                {c.snippet ? (
                                    <div className='border-border text-foreground mt-1.5 border-l-2 pl-2 text-xs [&_*]:break-words'>
                                        <Markdown>{c.snippet}</Markdown>
                                    </div>
                                ) : null}
                            </div>
                        ))
                    )}
                </div>
            </aside>
        </div>
    )
}
